import type {
  EarlyCheckinKind,
  EarlyCheckinStage,
  EarlyCheckinStatus,
} from '../types/earlyCheckin';

const STAGES: readonly EarlyCheckinStage[] = [
  'awaiting_fup',
  'fup_8_days',
  'fup_15_days',
  'closed',
];

const STATUSES: readonly EarlyCheckinStatus[] = [
  'scheduled',
  'open',
  'waiting_performance',
  'done',
];

const KINDS: readonly EarlyCheckinKind[] = ['day_8', 'day_15'];

export function isEarlyCheckinStage(value: string): value is EarlyCheckinStage {
  return STAGES.some((stage) => stage === value);
}

export function isEarlyCheckinStatus(value: string): value is EarlyCheckinStatus {
  return STATUSES.some((status) => status === value);
}

export function isEarlyCheckinKind(value: string): value is EarlyCheckinKind {
  return KINDS.some((kind) => kind === value);
}

export function firstNameFromFullName(fullName: string | null | undefined): string {
  const trimmed = (fullName ?? '').trim();
  if (!trimmed) {
    return '';
  }

  return trimmed.split(/\s+/)[0] ?? '';
}

export function interpolateEarlyCheckinName(
  template: string,
  fullName: string | null | undefined,
): string {
  const firstName = firstNameFromFullName(fullName);
  if (!firstName) {
    return template;
  }

  return template.split('[nome]').join(firstName);
}

export function buildWhatsAppUrl(
  countryCode: string | null | undefined,
  phoneNumber: string | null | undefined,
): string | null {
  const callingCode = (countryCode || '55').replace(/\D/g, '');
  const digits = (phoneNumber ?? '').replace(/\D/g, '');
  if (!digits) {
    return null;
  }

  return `https://wa.me/${callingCode}${digits}`;
}

export function resolveCurrentEarlyCheckinKind(
  day8Status: EarlyCheckinStatus | null | undefined,
): EarlyCheckinKind {
  return day8Status === 'done' ? 'day_15' : 'day_8';
}

export function isEarlyCheckinSuccessful(
  status: EarlyCheckinStatus | null | undefined,
): boolean {
  return status === 'done';
}

export function getEarlyCheckinSuccessLabel(
  kind: EarlyCheckinKind,
  status: EarlyCheckinStatus | null | undefined,
): string {
  const periodLabel = kind === 'day_8' ? '8 dias' : '15 dias';
  return isEarlyCheckinSuccessful(status)
    ? `${periodLabel}: sucesso`
    : `${periodLabel}: sem sucesso`;
}

export function resolveEarlyCheckinStage(params: {
  day8Status: EarlyCheckinStatus | null | undefined;
  day8DueOn: string | null | undefined;
  day15Status: EarlyCheckinStatus | null | undefined;
  day15DueOn: string | null | undefined;
  today: string;
}): EarlyCheckinStage {
  const day8Due = (params.day8DueOn ?? '').slice(0, 10);
  const day15Due = (params.day15DueOn ?? '').slice(0, 10);
  const today = params.today.slice(0, 10);

  if (params.day8Status === 'done' && params.day15Status === 'done') {
    return 'closed';
  }

  if (params.day8Status !== 'done' && day8Due && day8Due <= today) {
    return 'fup_8_days';
  }

  if (
    params.day8Status === 'done'
    && params.day15Status !== 'done'
    && day15Due
    && day15Due <= today
  ) {
    return 'fup_15_days';
  }

  return 'awaiting_fup';
}

export function canStartEarlyCheckin(
  status: EarlyCheckinStatus,
  stage: EarlyCheckinStage,
): boolean {
  return status === 'scheduled' && stage !== 'closed';
}

export function canEscalateEarlyCheckin(status: EarlyCheckinStatus): boolean {
  return status === 'open';
}

export function canCloseEarlyCheckin(status: EarlyCheckinStatus): boolean {
  return status === 'open' || status === 'waiting_performance';
}
